import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowRightLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  type AgentAccessScope, type AgentConversation, type AgentHandoffDestination, type AgentHandoffReview, type AgentHandoffResult,
  getListAgentConversationsQueryKey, useConfirmAgentHandoff, useListAgentHandoffDestinations, useReviewAgentHandoff
} from '@cdf/api-client-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { workspaceError } from './workspace-utils';

const PAGE = 10;

export function sameSelection(a: readonly string[], b: readonly string[]) {
  return a.length === b.length && a.every(id => b.includes(id));
}

interface HandoffProps {
  scope: AgentAccessScope;
  mode: 'internal' | 'public_research';
  chat: AgentConversation;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Review-first hand-off of a private saved chat to another assistant. Nothing is copied until the person confirms. */
export function AgentChatHandoff({ scope, mode, chat, open, onOpenChange }: HandoffProps) {
  const client = useQueryClient();
  const [search, setSearch] = React.useState('');
  const [offset, setOffset] = React.useState(0);
  const [destination, setDestination] = React.useState<AgentHandoffDestination>();
  const [review, setReview] = React.useState<AgentHandoffReview>();
  const [excluded, setExcluded] = React.useState<string[]>([]);
  const [result, setResult] = React.useState<AgentHandoffResult>();
  const [error, setError] = React.useState<string>();
  const requestId = React.useRef<string>('');
  const params = { scopeKind: scope.kind, ...(scope.tenantId ? { tenantId: scope.tenantId } : {}), ...(scope.entityId ? { entityId: scope.entityId } : {}),
    mode, ...(search.trim() ? { search: search.trim() } : {}), limit: PAGE, offset };
  const destinations = useListAgentHandoffDestinations(params, { query: { enabled: open && !review && !result, retry: false } as never });
  const prepare = useReviewAgentHandoff({ mutation: { retry: false } });
  const confirm = useConfirmAgentHandoff({ mutation: { retry: false } });
  const busy = prepare.isPending || confirm.isPending;

  React.useEffect(() => {
    if (open) return;
    setSearch(''); setOffset(0); setDestination(undefined); setReview(undefined); setExcluded([]); setResult(undefined); setError(undefined);
  }, [open]);

  async function runReview(target: AgentHandoffDestination, exclude: string[]) {
    setError(undefined);
    try {
      const next = await prepare.mutateAsync({ id: chat.id, data: { accessScope: scope, destination: target.target, excludedMessageIds: exclude } });
      setDestination(target); setReview(next); setExcluded(next.excludedMessageIds);
      requestId.current = crypto.randomUUID();
    } catch (failure) { setError(workspaceError(failure)); }
  }
  async function runConfirm() {
    if (!review || !sameSelection(excluded, review.excludedMessageIds)) return;
    setError(undefined);
    try {
      const receipt = await confirm.mutateAsync({ id: chat.id, data: { accessScope: scope, handoffId: review.handoffId, reviewToken: review.reviewToken,
        excludedMessageIds: review.excludedMessageIds, requestId: requestId.current } });
      setResult(receipt);
      await client.invalidateQueries({ queryKey: getListAgentConversationsQueryKey() });
    } catch (failure) { setError(workspaceError(failure)); }
  }
  const toggle = (id: string) => setExcluded(current => current.includes(id) ? current.filter(item => item !== id) : [...current, id]);
  const selectionChanged = review ? !sameSelection(excluded, review.excludedMessageIds) : false;
  const included = review ? review.messages.filter(message => !excluded.includes(message.id)).length : 0;

  return <Dialog open={open} onOpenChange={next => { if (!busy) onOpenChange(next); }}>
    <DialogContent className="flex max-h-[85dvh] flex-col gap-3 overflow-hidden">
      <DialogHeader>
        <DialogTitle>Hand off chat</DialogTitle>
        <DialogDescription>Copy “{chat.title}” to another assistant. You review which messages move before anything is copied; this chat stays as it is.</DialogDescription>
      </DialogHeader>
      {result ? <div role="status" className="space-y-2 text-sm">
        <p>Handed off {result.copiedMessages} message{result.copiedMessages === 1 ? '' : 's'} to {review?.destinationLabel}.</p>
        <p className="text-muted-foreground">Open that assistant to continue in “{result.conversation.title}”.</p>
      </div> : review && destination ? <div className="flex min-h-0 flex-1 flex-col gap-2">
        <p className="text-sm">To <span className="font-medium">{review.destinationLabel}</span></p>
        <ul className="list-disc space-y-0.5 pl-5 text-xs text-muted-foreground">{review.notes.map(note => <li key={note}>{note}</li>)}</ul>
        <fieldset className="min-h-0 flex-1 space-y-1 overflow-y-auto rounded-md border p-2" aria-label="Messages to hand off">
          {review.messages.map(message => <label key={message.id} className="flex items-start gap-2 rounded p-1 text-xs hover:bg-muted/50">
            <input type="checkbox" className="mt-0.5 size-4 shrink-0 accent-foreground" checked={!excluded.includes(message.id)} disabled={busy} onChange={() => toggle(message.id)} />
            <span className="min-w-0 flex-1"><span className="block break-words">{message.preview}</span>
              <span className="text-muted-foreground">{new Date(message.createdAt).toLocaleString()}{message.attachmentCount > 0 ? ` · ${message.attachmentCount} attachment(s) stay behind` : ''}</span></span>
          </label>)}
        </fieldset>
        <p className="text-xs text-muted-foreground" aria-live="polite">{included} of {review.messages.length} messages selected. Review expires {new Date(review.expiresAt).toLocaleTimeString()}.</p>
        {selectionChanged && <p className="text-xs">You changed the selection. Update the review before confirming.</p>}
      </div> : <div className="flex min-h-0 flex-1 flex-col gap-2">
        <Input aria-label="Search assistants" placeholder="Search assistants" value={search} maxLength={120} onChange={event => { setSearch(event.target.value); setOffset(0); }} />
        {destinations.isPending && <p role="status" className="text-sm">Loading assistants…</p>}
        {destinations.isError && <div role="alert" className="text-sm"><p>{workspaceError(destinations.error)}</p><Button size="sm" variant="outline" onClick={() => void destinations.refetch()}>Retry</Button></div>}
        {destinations.data?.items.length === 0 && <p className="text-sm text-muted-foreground">No other assistants are available to you.</p>}
        <div className="min-h-0 flex-1 space-y-1 overflow-y-auto">
          {destinations.data?.items.map(item => <Button key={item.key} type="button" variant="ghost" className="h-auto w-full justify-start whitespace-normal py-2 text-left" disabled={busy} onClick={() => void runReview(item, [])}>
            <span className="min-w-0"><span className="block text-sm">{item.label}</span><span className="text-xs text-muted-foreground">{item.assistant === 'research' ? 'Research' : 'Assistant'}</span></span>
          </Button>)}
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-muted-foreground">{destinations.data?.total ? `${offset + 1}–${Math.min(offset + PAGE, destinations.data.total)} of ${destinations.data.total}` : ''}</span>
          <span className="flex gap-1">
            <Button size="icon" variant="outline" className="size-8" aria-label="Previous assistants" disabled={offset === 0 || destinations.isFetching} onClick={() => setOffset(Math.max(0, offset - PAGE))}><ChevronLeft className="size-3" /></Button>
            <Button size="icon" variant="outline" className="size-8" aria-label="Next assistants" disabled={!destinations.data || offset + PAGE >= destinations.data.total || destinations.isFetching} onClick={() => setOffset(offset + PAGE)}><ChevronRight className="size-3" /></Button>
          </span>
        </div>
      </div>}
      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
      <DialogFooter className="gap-2">
        {result ? <Button onClick={() => onOpenChange(false)}>Done</Button> : review && destination ? <>
          <Button variant="ghost" disabled={busy} onClick={() => { setReview(undefined); setDestination(undefined); setExcluded([]); }}>Choose another assistant</Button>
          {selectionChanged
            ? <Button variant="outline" disabled={busy || included === 0} onClick={() => void runReview(destination, excluded)}>Update review</Button>
            : <Button disabled={busy} onClick={() => void runConfirm()}><ArrowRightLeft className="size-4" />Confirm hand-off</Button>}
        </> : <Button variant="ghost" disabled={busy} onClick={() => onOpenChange(false)}>Cancel</Button>}
      </DialogFooter>
    </DialogContent>
  </Dialog>;
}
